
import React from 'react'
import { Button } from '@/components/ui/button'
import { Plus, Star, Archive, ArrowUp } from 'lucide-react'

interface Task {
  id: string
  title: string
  module?: string
  priority?: boolean
  badge?: string
  completed?: boolean
  description?: string
  domain?: string
  status?: string
}

interface TaskCardProps {
  task: Task
  showBadge?: boolean
  showActions?: boolean
  actionType?: 'add' | 'priority' | 'archive'
  onAction?: (taskId: string, actionType: string) => void
  showArchiveButton?: boolean
}

const TaskCard: React.FC<TaskCardProps> = ({ 
  task, 
  showBadge = false, 
  showActions = false,
  actionType,
  onAction,
  showArchiveButton = false
}) => {
  if (!task || !task.title) {
    console.error('TaskCard received invalid task prop:', task)
    return null
  }

  const handleAction = () => {
    if (onAction && actionType) {
      onAction(task.id, actionType)
    }
  }

  const handleArchive = () => {
    if (onAction) {
      onAction(task.id, 'archive')
    }
  }

  const getActionButton = () => {
    switch (actionType) {
      case 'add':
        return (
          <Button size="sm" variant="outline" onClick={handleAction}>
            <Plus className="w-3 h-3 mr-1" />
            Add
          </Button>
        )
      case 'priority':
        return (
          <Button size="sm" variant="outline" onClick={handleAction}>
            <Star className="w-3 h-3 mr-1" />
            Priority
          </Button>
        )
      case 'archive':
        return (
          <Button size="sm" variant="outline" onClick={handleAction}>
            <Archive className="w-3 h-3 mr-1" />
            Archive
          </Button>
        )
      default:
        return null
    }
  }

  return (
    <div className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          {showBadge && task.badge && (
            <span className="inline-block px-2 py-1 text-xs bg-blue-100 text-blue-600 rounded-full mb-2">
              {task.badge}
            </span>
          )}
          <h4 className="text-sm font-medium text-gray-900 break-words leading-relaxed">{task.title}</h4>
          {task.description && (
            <p className="text-xs text-gray-600 mt-1 break-words">{task.description}</p>
          )}
          {task.module && (
            <p className="text-xs text-gray-500 mt-1">{task.module}</p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {task.priority && (
            <span className="w-2 h-2 bg-red-500 rounded-full"></span>
          )}
          {showActions && getActionButton()}
          {showArchiveButton && (
            <Button size="sm" variant="outline" onClick={handleArchive}>
              <Archive className="w-3 h-3 mr-1" />
              Archive
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

export default TaskCard
